// The sheet (web/sheets.js openSheet / renderSheet): an event's card, a group's events, My events or Profile. Desktop:
// in the side panel, in place of the list. Phone width: a sheet up from the bottom, edge to edge with only its top
// corners rounded; an event's card opens as its peek and drags (or taps) up to the full card, down to shrink or
// close. It slides in, and out when closed (it keeps drawing what it showed until it's gone).
import { useEffect, useRef, useState } from "react";
import { closeSheet, openSheet, useSheet, viewStore, type Sheet as SheetState } from "../../state/view";
import { eventById, useFeed } from "../../state/feed";
import { useProfileStore } from "./useProfileEvents";
import { ProfileSheet } from "../profile/slots";
import { ErrorBoundary } from "../../app/ErrorBoundary";
import { Card } from "./Card";
import { Results } from "./Results";
import styles from "./Sheet.module.css";

export function Sheet() {
  const sheet = useSheet();
  useFeed((s) => s.byId); // an event's card follows its event (and closes if it's gone)
  useFeed((s) => s.linked);
  useProfileStore();
  const [shown, setShown] = useState<SheetState | null>(sheet);
  const [leaving, setLeaving] = useState(false);
  const el = useRef<HTMLDivElement>(null);
  const lastKind = useRef<string | null>(null);

  useEffect(() => {
    if (sheet) { setShown(sheet); setLeaving(false); }
    else if (shown) setLeaving(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheet]);
  // A different sheet slides in from the top of its scroll.
  useEffect(() => {
    const key = sheet ? `${sheet.kind}|${"id" in sheet ? sheet.id : ""}` : null;
    if (key && key !== lastKind.current && el.current) el.current.scrollTop = 0;
    lastKind.current = key;
  }, [sheet]);

  // Phone width: drag the card's top up for the full card, down to shrink or close it; a tap opens it.
  const startY = useRef<number | null>(null);
  useEffect(() => {
    const up = (ev: PointerEvent) => {
      if (startY.current == null) return;
      const dy = ev.clientY - startY.current;
      startY.current = null;
      const s = viewStore.getState().sheet;
      if (s?.kind !== "event") return;
      if (dy < -25) openSheet({ ...s, full: true });
      else if (dy > 40) { if (s.full) openSheet({ ...s, full: false }); else closeSheet(); }
      else if (Math.abs(dy) < 6 && (ev.target as Element).closest?.("#card-peek") && !s.full) openSheet({ ...s, full: true });
    };
    addEventListener("pointerup", up);
    return () => removeEventListener("pointerup", up);
  }, []);

  // The open event left the feed (retired by the server): close its card rather than leave an empty sheet.
  const gone = sheet?.kind === "event" && !eventById(sheet.id);
  useEffect(() => { if (gone) closeSheet(); }, [gone]);

  const s = sheet ?? shown;
  if (!s) return null;
  const event = s.kind === "event" ? eventById(s.id) : null;
  if (s.kind === "event" && !event) return null;
  const peek = s.kind === "event" && !s.full;
  const cls = [styles.sheet, leaving && styles.leaving, peek ? styles.peek : styles.tall].filter(Boolean).join(" ");
  return (
    <div ref={el} id="sheet" className={cls} data-kind={s.kind} data-peek={peek || undefined}
      onAnimationEnd={() => { if (leaving) { setLeaving(false); setShown(null); } }}
      onPointerDown={(ev) => {
        const t = ev.target as Element;
        if (s.kind === "event" && t.closest("#card-peek, #card-expand") && !t.closest("button:not(#card-expand), a")) startY.current = ev.clientY;
      }}>
      <div className={styles.body}>
        <ErrorBoundary region={s.kind === "event" ? "card" : s.kind}>
          {s.kind === "event" ? <Card event={event!} full={!!s.full} />
            : s.kind === "results" ? <Results title={s.title} ids={s.ids} />
            : <ProfileSheet kind={s.kind} />}
        </ErrorBoundary>
      </div>
    </div>
  );
}
